import { LoadingSpinner } from '../utils/LoadingSpinner';
import Button from '@mui/material/Button';

interface GRequestsTableProps {
    requests: any[];
    loading?: boolean;
    error?: string | null;
    onApprove?: (userId: string | number, objectId: string | number, row?: any) => any;
    onRevoke?: (userId: string | number, objectId: string | number, row?: any) => any;
    approving?: boolean | null;
    user?: any;
}

export default function GRequestsTable({
    requests,
    loading = false,
    error = null,
    onApprove,
    onRevoke,
    approving = false,
    user,
}: GRequestsTableProps) {
    const requestColumns = requests.length > 0 ? Object.keys(requests[0]) : [];
    const hiddenColumns = ['user_id', 'object_id', 'object_owner_id'];
    const finalColumns = requestColumns.filter((c) => !hiddenColumns.includes(String(c).toLowerCase()));
    if (!finalColumns.includes('nda_request')) finalColumns.push('nda_request');
    if (!finalColumns.includes('nda_signed')) finalColumns.push('nda_signed');

    const isAdmin = !!(user && (user.role === 1 || String(user.role) === '1'));
    const showAction = isAdmin || ((onApprove || onRevoke) && approving !== null);

    const getNumericStatus = (row: any): number | null => {
        const rawStatus = row?.status;
        if (rawStatus === null || rawStatus === undefined || rawStatus === '') return null;
        const parsed = Number(rawStatus);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const getTextStatus = (row: any): string => {
        const rawStatus = row?.status;
        if (rawStatus === null || rawStatus === undefined) return '';
        return String(rawStatus).toLowerCase();
    };

    const isApproved = (row: any): boolean => {
        const numeric = getNumericStatus(row);
        if (numeric === 2) return true;
        const text = getTextStatus(row);
        return text.includes('approved');
    };

    if (loading) return <LoadingSpinner text="Loading requests..." />;
    if (error) return <LoadingSpinner text={String(error)} />;

    if (!Array.isArray(requests) || requests.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <div style={{ fontSize: 18, marginBottom: 8 }}>No requests found</div>
                <div style={{ fontSize: 14 }}>There are no requests matching your filter or user.</div>
            </div>
        );
    }

    return (
        <table className="elegant-table" border={1} cellPadding={8}>
            <thead>
                <tr>
                    {finalColumns.map((column) => (
                        <th key={column}>{column}</th>
                    ))}
                    {showAction && <th>Action</th>}
                </tr>
            </thead>
            <tbody>
                {requests.map((row, rowIndex) => (
                    <tr key={row.id ?? row.request_id ?? row.ticket ?? rowIndex}>
                        {finalColumns.map((column) => {
                            const cellValue = row[column];
                            let cellClass = '';
                            try {
                                if (String(column).toLowerCase() === 'status') {
                                    const numeric = getNumericStatus(row);
                                    const textVal = cellValue === null || cellValue === undefined ? '' : String(cellValue).toLowerCase();
                                    if (numeric === 2 || textVal.includes('approved')) {
                                        cellClass = 'status-approved';
                                    }
                                }
                            } catch (e) {
                                // ignore
                            }

                            return (
                                <td key={`${rowIndex}-${column}`} className={cellClass}>
                                    {(() => {
                                        if (String(column).toLowerCase() === 'nda_request' || String(column).toLowerCase() === 'nda_signed') {
                                            const val = cellValue === true || cellValue === 1 || String(cellValue).toLowerCase() === 'true' || String(cellValue) === '1';
                                            return val ? 'TRUE' : 'FALSE';
                                        }
                                        return cellValue === null || cellValue === undefined ? '—' : String(cellValue);
                                    })()}
                                </td>
                            );
                        })}

                        {showAction && (
                            <td>
                                {(() => {
                                    const userId = row.user ?? row.user_id;
                                    const objectId = row.object_id ?? row.object;

                                    if (isApproved(row) && onRevoke) {
                                        return (
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={async () => {
                                                    console.debug('[GRequestsTable] Revoke clicked', { userId, objectId, row });
                                                    try {
                                                        const res = onRevoke(userId, objectId, row);
                                                        // Synchronous mode: do not await Promises here — caller handles async work.
                                                        console.debug('[GRequestsTable] onRevoke returned', { res });
                                                    } catch (err) {
                                                        console.error('[GRequestsTable] onRevoke handler error', err);
                                                    }
                                                }}
                                                disabled={!!approving}
                                            >
                                                Revoke
                                            </Button>
                                        );
                                    }

                                    if (!isApproved(row) && onApprove) {
                                        return (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={async () => {
                                                    console.debug('[GRequestsTable] Approve clicked', { userId, objectId, row });
                                                    try {
                                                        const res = onApprove(userId, objectId, row);
                                                        // Synchronous mode: do not await Promises here — caller handles async work.
                                                        if (res && typeof (res as any).then === 'function') {
                                                            console.debug('[GRequestsTable] onApprove returned Promise — not awaiting (sync mode)', { res });
                                                        } else {
                                                            console.debug('[GRequestsTable] onApprove returned synchronously', { res });
                                                        }
                                                    } catch (err) {
                                                        console.error('[GRequestsTable] onApprove handler error', err);
                                                    }
                                                }}
                                                disabled={!!approving}
                                            >
                                                Approve
                                            </Button>
                                        );
                                    }

                                    return '—';
                                })()}
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
